import ButtonSelector from 'Containers/Common/CustomSelector/ButtonSelector';
import clx from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useWindowSize from '../../../../utils/useWindowSize';
import TitleSectionBack from '../../../Common/TitleSectionBack';
import { paymentActions } from '../../store';
import { getBonifici, getPayments, getTotal } from '../../store/selectors';
import BonificiTable from '../BonificiTable';
import PaymentTable from '../PaymentTable';
import styles from './PaymentList.module.scss';
import PageInfo from '../PageInfo';
import { getActiveSalon } from 'Containers/Auth/store/selectors';
import dayjs from 'dayjs';
import PendingBonificiRequest from '../PendingBonificiRequest';
import { useTranslation } from 'react-i18next';
import { ArrowDownToLine, Wallet } from 'lucide-react';

const PAGE_SIZE = 20;

const buildDateRange = (periodDays) => {
  const to = dayjs().format('YYYY-MM-DD');
  if (!periodDays) {
    return { from: '2010-01-01', to };
  }
  return {
    from: dayjs().subtract(periodDays, 'day').format('YYYY-MM-DD'),
    to,
  };
};

const PaymentList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useWindowSize();
  const payments = useSelector(getPayments()) || [];
  const { total, balance, balanceRows } = useSelector(getTotal());
  const bonifici = useSelector(getBonifici()) || [];
  const activeSalon = useSelector(getActiveSalon());

  const [activeFilterId, setActiveFilterId] = useState(0);
  const [period, setPeriod] = useState(0);
  const [pagination, setPaginationState] = useState({
    skip: 0,
    limit: PAGE_SIZE,
  });

  const dateRange = useMemo(() => buildDateRange(period), [period]);
  const isBonificiTab = activeFilterId === 2;

  const setPaginationValue = useCallback((next) => {
    setPaginationState((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      if (!value) return prev;
      const skip = Number(value.skip) || 0;
      const limit = Number(value.limit) || PAGE_SIZE;
      if (prev.skip === skip && prev.limit === limit) return prev;
      return { skip, limit };
    });
  }, []);

  const handlePeriodChange = useCallback((nextPeriod) => {
    setPeriod(Number(nextPeriod) || 0);
    setPaginationState((prev) =>
      prev.skip === 0 ? prev : { ...prev, skip: 0 },
    );
  }, []);

  useEffect(() => {
    dispatch(
      paymentActions.GET_PAYMENTS.REQUEST(
        {
          from: dateRange.from,
          to: dateRange.to,
          cliente: null,
          limit: pagination.limit,
          offset: pagination.skip,
        },
        null,
        { skipLoader: true },
      ),
    );
  }, [
    dispatch,
    dateRange.from,
    dateRange.to,
    pagination.skip,
    pagination.limit,
  ]);

  useEffect(() => {
    dispatch(paymentActions.GET_BONIFICI.REQUEST({}, null, { skipLoader: true }));
  }, [dispatch]);

  const filtersButtons = [
    {
      name: t('payments.list.filters.toReceive'),
      id: 0,
      icon: Wallet,
    },
    {
      name: t('payments.list.filters.received'),
      id: 2,
      icon: ArrowDownToLine,
    },
  ];

  const dataFilterOptions = [
    {
      name: t('payments.list.periodFilters.lastWeek'),
      value: 7,
    },
    {
      name: t('payments.list.periodFilters.lastMonth'),
      value: 30,
    },
    {
      name: t('payments.list.periodFilters.lastYear'),
      value: 365,
    },
    {
      name: t('payments.list.periodFilters.all'),
      value: 0,
    },
  ];

  const filteredBonifici = useMemo(() => {
    const sent = bonifici.filter((item) => item.sent === 1);
    if (!period) return sent;
    const from = dayjs(dateRange.from).startOf('day');
    return sent.filter((item) => {
      const date = dayjs(item.data);
      return date.isValid() && !date.isBefore(from, 'day');
    });
  }, [bonifici, period, dateRange.from]);

  const paymentsPageCount = Math.ceil((Number(total) || 0) / pagination.limit) || 0;
  const paymentsEntityId = `payments-${period}-${dateRange.from}-${dateRange.to}`;
  const bonificiEntityId = `bonifici-${period}-${dateRange.from}`;

  return (
    <div
      className={clx(styles.payments_list_container, {
        [styles.payments_list_container_mobile]: isMobile,
      })}
    >
      <div className={styles.header}>
        <TitleSectionBack
          title={t('payments.list.title')}
          description={t('payments.list.description')}
        />
      </div>
      <PendingBonificiRequest bonifici={bonifici} />
      <PageInfo
        bonifici={bonifici}
        balanceRows={balanceRows}
        activeSalon={activeSalon}
        balance={balance}
      />
      <div className={styles.list_card}>
        <div className={styles.toolbar}>
          <div className={styles.filters_row}>
            {filtersButtons.map((filter) => {
              const Icon = filter.icon;
              const isActive = activeFilterId === filter.id;
              return (
                <button
                  key={filter.id}
                  type="button"
                  className={clx(styles.filter_chip, {
                    [styles.filter_chip_selected]: isActive,
                  })}
                  aria-pressed={isActive}
                  onClick={() => setActiveFilterId(filter.id)}
                >
                  <span className={styles.filter_chip_icon}>
                    <Icon size={16} strokeWidth={2.2} />
                  </span>
                  <span className={styles.filter_chip_name}>{filter.name}</span>
                </button>
              );
            })}
          </div>
          <div className={styles.period_wrap}>
            <ButtonSelector
              title=""
              setValue={handlePeriodChange}
              options={dataFilterOptions}
              value={period}
              backgroundColor="white"
              width={isMobile ? '100%' : 'fit-content'}
              marginLeft="0px"
            />
          </div>
        </div>

        {/* Entrambe montate: ogni tab tiene la propria pagina */}
        <div
          className={clx(styles.tab_panel, {
            [styles.tab_panel_hidden]: isBonificiTab,
          })}
          aria-hidden={isBonificiTab}
        >
          <PaymentTable
            setPage={setPaginationValue}
            total={paymentsPageCount}
            values={payments}
            isMobile={isMobile}
            entityId={paymentsEntityId}
          />
        </div>
        <div
          className={clx(styles.tab_panel, {
            [styles.tab_panel_hidden]: !isBonificiTab,
          })}
          aria-hidden={!isBonificiTab}
        >
          <BonificiTable
            values={filteredBonifici}
            isMobile={isMobile}
            entityId={bonificiEntityId}
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentList;
